import React, { useEffect, useContext, useMemo, useState } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { InfoFilled } from '@one-thd/sui-icons';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { useProductAddOnsFlow } from '../hooks/useProductAddOnsFlow';
import { HomeDepotProtectionPlan } from './add-ons/hdpp/HomeDepotProtectionPlan';
import { AttachLabor } from './add-ons/attach-labor/AttachLabor';
import { ProReferral } from './add-ons/pro-referral/ProReferral';
import { DualPath } from './add-ons/dual-path/DualPath';
import { SAMDualPath } from './add-ons/SAM-dual-path/SAMDualPath';
import { VirtualAssistance } from './add-ons/virtual-assistance/VirtualAssistance';
import { displayAttachLabor as attachLaborEligible } from '../utils/product-add-on-utils';
import { DUAL_PATH_CATEGORIES, getDualPathHeading } from './add-ons/dual-path/dualPathCategories';
import { dataModel } from './dataModel';
import * as styles from './product-add-ons.scss';
import './tooltip.scss';

const {
  string, func, bool, arrayOf, ...PropTypes
} = proptypes;

const ProductAddOns = ({
  itemId,
  fulfillment,
  availableFulfillments,
  onChange,
  showCards,
  hideProReferral,
  defaultProps,
  isTwoTileEnabled,
  isShowATC,
  zipCode,
  bodfsGrillsAssemblyEnabledFS,
  hideProtectionPlan,
  noPipSpacing
}) => {
  const theme = useTheme(ProductAddOns);
  const {
    hideExpertInHome, hideProductAddOns, hideAttachLabor, hideHomeDepotProtectionPlan,
    hideProReferralDisplayable, hideGarbageDisposalVirtualAssistance, hideInstallVirtualAssistance,
    hideVirtualAssistance, hideSAMDualPath, hideDualPath
  } = theme.props;

  const expData = useContext(ExperienceContext);
  const { channel } = expData;
  const [isProReferralDisplayable, setIsProReferralDisplayable] = useState(false);
  const storeId = useStoreId();
  const [errorProtectionPlanArray, setErrorProtectionPlan] = useState([]);

  const {
    loading,
    data,
    optionsForProtectionPlan,
    isProReferral,
    isAssembly,
    isDualPath,
    isInstallationSku,
    inStoreAssemblyAvailable,
    bodfsAssemblyEligible,
    isSAMDualPath,
    isVirtualAssistance,
    discontinued,
    applicableFulfillments
  } = useProductAddOnsFlow({ itemId, storeId, zipCode });

  const shouldShowProtectionPlan = (
    optionsForProtectionPlan?.protectionPlanSkuArr?.length > 0
    && errorProtectionPlanArray.length < optionsForProtectionPlan.protectionPlanSkuArr.length
    && optionsForProtectionPlan?.variables?.price > 0
    && !discontinued && !defaultProps.dualPath
    && !hideProtectionPlan
  );

  const isBodfsGrillsAssemblyEnabledFS = useConfigService('bodfsGrillsAssemblyEnabled') || bodfsGrillsAssemblyEnabledFS;
  const virtualAssistantCategories = useConfigService('virtualAssistantCategories') || '';
  const bodfsGrillsAssemblyEnabled = isBodfsGrillsAssemblyEnabledFS === 1 || isBodfsGrillsAssemblyEnabledFS === '1';
  const isInstallVirtualAssistance = data?.serviceAddOns?.installationDetails?.virtualAssistanceFlag || false;
  const installationCategory = data?.serviceAddOns?.installationDetails?.installationCategory || null;

  const isGarbageDisposalVirtualAssistance = data?.product?.info?.productDepartment === '29'
  && data?.product?.info?.classNumber === '14'
    ? !data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe
    : false;

  // Virtual Associates for Built-in Appliances (Cooktops & Wall Ovens)
  const classConfig = data?.product?.info?.productDepartment +'-'+ data?.product?.info?.classNumber +'-'+ data?.product?.info?.subClassNumber;
  const isVirtualAssociatesBuiltIns = `,${virtualAssistantCategories},`.includes(`,${classConfig},`);

  const displayAttachLabor = !hideProtectionPlan && !hideAttachLabor && attachLaborEligible({
    isInstallationSku,
    inStoreAssemblyAvailable,
    availableFulfillments,
    currentFulfillment: fulfillment,
    isTwoTileEnabled,
    // Flag from backend on if the item is eligible for BODFS assembly
    bodfsAssemblyEligible,
    // FS for BODFS Grill Assembly
    bodfsGrillsAssemblyEnabled,
    applicableFulfillments
  });

  const { installationProgramCategory } = data?.serviceAddOns?.sellFurnishInstall || {};
  const bathRemodelCategory = ['BATHROOM_REMODEL'].includes(DUAL_PATH_CATEGORIES[installationProgramCategory]);

  useEffect(() => {
    setIsProReferralDisplayable(
      !bathRemodelCategory && !hideProReferral && !displayAttachLabor && (isProReferral || isAssembly)
    );
  }, [bathRemodelCategory, hideProReferral, displayAttachLabor, isProReferral, isAssembly]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-add-ons.ready', {
      isProReferralDisplayable
    });
  }, [isProReferralDisplayable]);

  const getCategory = (serviceName) => {
    const PR_INSTALL = 'PRInstall';
    const PAINTER = 'Painter';
    const ASSEMBLY = 'Assembly';

    if (serviceName === 'interior_painting' || serviceName === 'exterior_painting') return PAINTER;
    if (serviceName === 'barbeque_assembly' || serviceName === 'outdoor_furniture_assembly') return ASSEMBLY;

    return PR_INSTALL;
  };

  const { installProcess, priceIncludes } = useMemo(() => {
    const doItForMe = data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe;
    return {
      priceIncludes: (doItForMe?.priceIncludes || []).filter(
        (priceInclude) => priceInclude.shortText
      ),
      installProcess: (doItForMe?.installProcess || []).filter(
        (installProcessItem) => installProcessItem.longText
      ),
    };
  }, [data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe]);

  if (!data || loading) {
    return null;
  }

  if (!displayAttachLabor && !data?.serviceAddOns
    && optionsForProtectionPlan?.protectionPlanSkuArr?.length === 0
    && !isSAMDualPath && !isVirtualAssistance && !isInstallVirtualAssistance
    && !isGarbageDisposalVirtualAssistance && !isVirtualAssociatesBuiltIns) {
    return null;
  }

  const { serviceName } = data?.serviceAddOns?.proReferrals?.[0] || {};
  const { attachLabor } = data?.serviceAddOns || {};
  const installSkus = attachLabor?.installSkus?.[0] || {};
  const { serviceType, installSku } = installSkus || {};
  const { price: installRetailPrice } = attachLabor?.installSkus?.[0]?.installRetailPrice || {};

  const modelNumber = data?.product?.identifiers?.modelNumber || {};
  const { leadGenUrl } = data?.product?.details?.installation || {};

  const type = serviceType?.toLowerCase();

  let cx = classNames.bind(styles);

  let wrapperClasses = '';
  if (showCards) {
    wrapperClasses = cx({
      'product-add-ons': !showCards,
      'product-add-ons--card-background': showCards,
    });
  }
  const isMobile = channel === 'mobile';
  const cardClasses = classNames(
    showCards && 'sui-bg-primary sui-rounded-sm sui-max-w-full sui-overflow-hidden',
    showCards && 'sui-empty:sui-p-0 sui-empty:sui-mb-0',
    showCards && !noPipSpacing && 'sui-p-3 sui-mb-4',
    showCards && noPipSpacing && 'sui-p-0',
    showCards && noPipSpacing && (isMobile ? 'sui-mb-4' : 'sui-mb-8')
  );

  const oosAlertMsg = (
    <p className="sui-flex sui-py-5 sui-text-info">
      <div className="sui-mt-1"><InfoFilled size="small" /></div>
      <div className="sui-ml-2">
        <b>You&apos;ve Still Got Options!</b>&nbsp;You can request in-home installation
        services for a similar product. This item is currently out of stock.
      </div>
    </p>
  );

  const isOutOfStockOnline = availableFulfillments.length === 0;

  /** themeHideExpertInHome will be deprecated in next major version */
  const themeHideExpertInHome = isDualPath && hideExpertInHome
    && getDualPathHeading(installationProgramCategory) === 'Get Expert In-Home Installation';

  if (themeHideExpertInHome || hideProductAddOns) return null;

  return (
    <section className={wrapperClasses} data-component="ProductAddOns">
      {!hideDualPath && isDualPath && (
        <div>{isOutOfStockOnline && (oosAlertMsg)}
          <div className={cardClasses}>
            <DualPath
              channel={channel}
              onChange={onChange}
              itemId={itemId}
              category={installationProgramCategory}
              serviceUrl={leadGenUrl}
              value={defaultProps?.dualPath}
            />
          </div>
        </div>
      )}

      {!hideSAMDualPath && isSAMDualPath && (
        <div>{isOutOfStockOnline && (oosAlertMsg)}
          <div className={cardClasses}>
            <SAMDualPath
              channel={channel}
              onChange={onChange}
              itemId={itemId}
              modelNumber={modelNumber}
            />
          </div>
        </div>
      )}

      {!isOutOfStockOnline && isShowATC && (
        <>
          {displayAttachLabor && (
            <div className={cardClasses}>
              <AttachLabor
                channel={channel}
                onChange={onChange}
                itemId={installSku}
                type={type}
                price={installRetailPrice}
                installProcess={installProcess}
                priceIncludes={priceIncludes}
                fulfillment={fulfillment}
                availableFulfillments={availableFulfillments}
                isInstallationSku={isInstallationSku}
                inStoreAssemblyAvailable={inStoreAssemblyAvailable}
                // Item is eligible for bodfs assembly and FS is ON
                bodfsAssemblyEligible={bodfsAssemblyEligible && bodfsGrillsAssemblyEnabled}
                isTwoTileEnabled={isTwoTileEnabled}
                zipCode={zipCode}
                applicableFulfillments={applicableFulfillments}
              />
            </div>
          )}

          {!hideVirtualAssistance && isVirtualAssistance && !isInstallVirtualAssistance && !isGarbageDisposalVirtualAssistance && (
            <div id="mva-buybox" className={cardClasses}>
              <VirtualAssistance
                channel={channel}
                onChange={onChange}
                itemId={itemId}
                value={defaultProps?.virtualAssistance}
              />
            </div>
          )}

          {!hideInstallVirtualAssistance && isInstallVirtualAssistance && (
            <div id="mva-buybox" className={cardClasses}>
              <VirtualAssistance
                channel={channel}
                onChange={onChange}
                itemId={itemId}
                isInstallVirtualAssistance={isInstallVirtualAssistance}
                installationCategory={installationCategory}
              />
            </div>
          )}

          {!hideGarbageDisposalVirtualAssistance && isGarbageDisposalVirtualAssistance && (
            <div id="mva-buybox" className={cardClasses}>
              <VirtualAssistance
                channel={channel}
                onChange={onChange}
                itemId={itemId}
                isGarbageDisposalVirtualAssistance
              />
            </div>
          )}

          {isVirtualAssociatesBuiltIns && (
            <div id="mva-buybox" className={cardClasses}>
              <VirtualAssistance
                channel={channel}
                onChange={onChange}
                itemId={itemId}
                isVirtualAssociatesBuiltIns
                installationCategory="Built-in Appliances"
              />
            </div>
          )}

          {!hideProReferralDisplayable && isProReferralDisplayable && (
            <div className={cardClasses}>
              <ProReferral
                onChange={onChange}
                channel={channel}
                category={getCategory(serviceName)}
                isAssembly={isAssembly}
              />
            </div>
          )}

          {!window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.isAPOFPOZipCode?.()
            && !hideHomeDepotProtectionPlan
            && shouldShowProtectionPlan && (
            <div className={cardClasses}>
              <HomeDepotProtectionPlan
                channel={channel}
                onChange={onChange}
                optionsForProtectionPlan={optionsForProtectionPlan}
                setErrorProtectionPlan={setErrorProtectionPlan}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

ProductAddOns.displayName = 'ProductAddOns';

ProductAddOns.defaultProps = {
  availableFulfillments: [],
  fulfillment: null,
  hideProReferral: false,
  showCards: null,
  defaultProps: { virtualAssistance: false, dualPath: false },
  isTwoTileEnabled: false,
  isShowATC: true,
  zipCode: '',
  bodfsGrillsAssemblyEnabledFS: 0,
  hideProtectionPlan: false,
  noPipSpacing: false
};
ProductAddOns.propTypes = {
  /** Product Number */
  itemId: string.isRequired,
  /** Function to call when checkbox is clicked */
  onChange: func.isRequired,
  /** Fulfillment method selected */
  fulfillment: string,
  /** List of currently available fulfillment types  */
  availableFulfillments: arrayOf(string),
  /** Boolean to remove the Pro referral component */
  hideProReferral: bool,
  showCards: bool,
  defaultProps: PropTypes.shape({
    virtualAssistance: PropTypes.bool,
    dualPath: PropTypes.bool,
  }),
  /** Determine if the incoming fulfillment is a 2 Tile */
  isTwoTileEnabled: bool,
  isShowATC: bool,
  zipCode: string,
  bodfsGrillsAssemblyEnabledFS: PropTypes.number,
  hideProtectionPlan: bool,
  noPipSpacing: bool
};

ProductAddOns.themeProps = {
  hideExpertInHome: bool,
  hideProductAddOns: bool,
  hideAttachLabor: bool,
  hideHomeDepotProtectionPlan: bool,
  hideProReferralDisplayable: bool,
  hideGarbageDisposalVirtualAssistance: bool,
  hideInstallVirtualAssistance: bool,
  hideVirtualAssistance: bool,
  hideSAMDualPath: bool,
  hideDualPath: bool
};
ProductAddOns.defaultThemeProps = {
  hideExpertInHome: false,
  hideProductAddOns: false,
  hideAttachLabor: false,
  hideHomeDepotProtectionPlan: false,
  hideProReferralDisplayable: false,
  hideGarbageDisposalVirtualAssistance: false,
  hideInstallVirtualAssistance: false,
  hideVirtualAssistance: false,
  hideSAMDualPath: false,
  hideDualPath: false
};

ProductAddOns.dataModel = dataModel;

export { ProductAddOns };
